function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function reportInputValidity(input, errorBlock) {
  if (!input || !errorBlock) return false;

  const type = input.type;
  const value = input.value.trim();
  let isValid = true;
  let errorType = "empty";

  isValid = value === "" ? false : true;

  switch (type) {
    case "email":
      if (value !== "" && !input.checkValidity()) {        
        isValid = false;
        errorType = "email"
      }
      break;
  }

  if (isValid) {
    input.removeAttribute("aria-invalid");
    errorBlock.innerText = "";
  } else {
    input.setAttribute("aria-invalid", true);
    errorBlock.innerText = window.errorStrings[errorType];
  }

  return isValid;
}

function togglePageScroll() {
  if (document.documentElement.style.overflow == "hidden") {
    document.documentElement.style.overflow = null;
  } else {
    document.documentElement.style.overflow = "hidden";
  }
}

// --- SECTIONS --- //

class Header {
  constructor(root) {
    this.root = root;
    this.isLoading = false;
    this.currentScrollTop = 0;
    this.headerBounds = {};

    this.mobileMenu = this.root.querySelector("#HeaderMenuDrawer");
    this.predictiveSearchDrawer = this.root.querySelector("#HeaderSearchDrawer");

    this.calcHeader();
    this.captureInitialBounds();

    this.root.addEventListener('click', this.onClick.bind(this));
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.predictiveSearchDrawer) {
      this.predictiveSearchDrawer.querySelector("input").addEventListener('input', debounce((e) => {
        this.onPredictiveChange(e.target.value);
      }, 500).bind(this));

      this.root.querySelector("#HeaderSearchInput").addEventListener('input', debounce((e) => {
        this.onPredictiveChange(e.target.value);
      }, 500).bind(this));
    }
  }

  captureInitialBounds() {
    const observer = new IntersectionObserver((entries, obs) => {
      const rect = entries[0].boundingClientRect;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      this.headerBounds = {
        top: rect.top + scrollY,
        bottom: rect.bottom + scrollY,
      };
      this.currentScrollTop = scrollY;
      if (scrollY > this.headerBounds.bottom) {
        this.root.classList.add('header--sticky', 'header--hidden');
      }
      obs.disconnect();
    });
    observer.observe(this.root);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const announcementHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')) || 0;
    const headerRect = this.root.getBoundingClientRect();
    document.documentElement.style.setProperty('--menu-height', `${(headerRect.bottom - Math.max(headerRect.top, 0)) + announcementHeight}px`);

    if (!this.headerBounds.bottom) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.root.classList.add('header--sticky', 'header--hidden');
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.root.classList.add('header--sticky');
      this.root.classList.remove('header--hidden');
    } else if (scrollTop <= Math.max(this.headerBounds.top - announcementHeight, 0)) {
      this.root.classList.remove('header--sticky', 'header--hidden');
    }

    this.currentScrollTop = scrollTop;
  }

  onPredictiveChange(query) {
    const predictiveSearchDrawerResults = this.predictiveSearchDrawer.querySelector("#HeaderSearchDrawerResults");
    const predictiveSearchResults = this.mobileMenu.querySelector("#HeaderSearchResults");
    if (this.isLoading) return;

    if (query === "") {
      predictiveSearchDrawerResults.innerHTML = "";
      predictiveSearchResults.innerHTML = "";
      return;
    }

    this.toggleLoading();

    const searchUrl = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query.trim())}&resources[type]=product,query&section_id=predictive-results`;

    fetch(searchUrl)
    .then(response => {
      if (!response.ok) {
        predictiveSearchDrawerResults.innerHTML = "";
        predictiveSearchResults.innerHTML = "";
        return;
      }

      return response.text();
    })
    .then(response => {
      const html = new DOMParser().parseFromString(response, 'text/html');
      predictiveSearchDrawerResults.innerHTML = html.querySelector("#shopify-section-predictive-results").innerHTML;
      predictiveSearchResults.innerHTML = html.querySelector("#shopify-section-predictive-results").innerHTML;
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading())
  }

  calcHeader() {
		const headerObserver = new ResizeObserver(() => {
      const headerRect = this.root.getBoundingClientRect();
			this.headerHeight = headerRect.height;

			document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
      document.documentElement.style.setProperty('--menu-height', `${headerRect.bottom}px`);
		});

		headerObserver.observe(this.root);
	}

  onClick(event) {
    if (!event.target.hasAttribute("data-action")) return;

    switch (event.target.dataset.action) {
      case "open-search":
        this.predictiveSearchDrawer.classList.add("open");
        document.documentElement.style.overflow = "hidden";
        break;
      case "close-search":
        this.predictiveSearchDrawer.classList.remove("open");
        document.documentElement.style.overflow = null;
        break;
      case "reset-search":
        this.root.querySelector("#HeaderSearchDrawerInput").value = "";
        this.root.querySelector("#HeaderSearchInput").value = "";
        this.onPredictiveChange("");
        break;
      case "toggle-menu":
        this.toggleMobileMenu();
        break;
      case "open-list":
        const sub = event.target.nextElementSibling;
        sub.classList.add("open");
        break;
      case "close-list":
        event.target.closest(".open").classList.remove("open");
        break;
      case "toggle-dropdown": {
        const dropdown = event.target.nextElementSibling;
        const isOpen = event.target.classList.toggle("open");
        event.target.setAttribute("aria-expanded", isOpen);
        dropdown.style.maxHeight = isOpen ? `${dropdown.scrollHeight}px` : null;
        break;
      }
    }
  }

  toggleMobileMenu() {
    if (!this.root.classList.contains("open")) {
      this.root.classList.add("open");
      document.documentElement.style.overflow = "hidden";
    } else {
      this.root.classList.remove("open");
      document.documentElement.style.overflow = null;
    }
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.root.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("header")) {
  new Header(document.querySelector("header"))
}

// Animation functionality 

document.addEventListener('DOMContentLoaded', () => {
  initJsAnimations();
});

function initJsAnimations(userOpts = {}) {
  const opts = {
    sectionPxToTrigger: 150,
    cardThreshold: 0.2,
    sectionDuration: 0.7,
    cardDuration: 0.6,
    cardDelayStep: 0.3,
    rootMargin: '0px',
    once: true,
    ...userOpts
  };

  const sections = Array.from(document.querySelectorAll('[js-animation]'));
  if (!sections.length) return;

  sections.forEach(sec => {
    const sectionPxToTrigger = getNumber(sec.dataset.sectionPx, opts.sectionPxToTrigger);
    const sectionDuration = getNumber(sec.dataset.sectionDuration, opts.sectionDuration);
    const cardDuration = getNumber(sec.dataset.cardDuration, opts.cardDuration);
    const cardDelayStep = getNumber(sec.dataset.cardDelayStep, opts.cardDelayStep);

    sec.style.setProperty('--section-dur', `${sectionDuration}s`);
    sec.style.setProperty('--card-dur', `${cardDuration}s`);
    sec.style.setProperty('--card-delay-step', `${cardDelayStep}s`);

    // Збережемо опції секції
    sec.__animOpts = { sectionPxToTrigger, cardDelayStep };
  });

  // Спостерігач для карток лишаємо (threshold по картці як було)
  const cardObserver = new IntersectionObserver(onCardsIntersect, {
    root: null,
    rootMargin: opts.rootMargin,
    threshold: buildThresholds(opts.cardThreshold)
  });

  sections.forEach(sec => {
    getCards(sec).forEach(card => {
      const order = Number(card.getAttribute('js-animation-block')) || 0;
      card.__order = order;
      card.__animated = false;
      cardObserver.observe(card);
    });
  });

  const pending = new Set(sections);
  let ticking = false;

  function checkSections() {
    ticking = false;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    pending.forEach(sec => {
      const rect = sec.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= viewportH) {

        if (!opts.once) {
          sec.classList.remove('is-animated');
          getCards(sec).forEach(c => {
            c.__animated = false;
            c.classList.remove('is-animated');
            c.style.transitionDelay = '';
          });
        }
        return;
      }

      const needPx = Math.min(sec.__animOpts.sectionPxToTrigger, rect.height);
      const firstChunkVisible = rect.top <= (viewportH - needPx);

      if (firstChunkVisible) {
        if (!sec.classList.contains('is-animated')) {
          sec.classList.add('is-animated');
        }
        if (opts.once) pending.delete(sec);
      } else if (!opts.once) {
        sec.classList.remove('is-animated');
        getCards(sec).forEach(c => {
          c.__animated = false;
          c.classList.remove('is-animated');
          c.style.transitionDelay = '';
        });
      }
    });
  }

  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(checkSections);
    }
  }

  onScrollOrResize();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  let rafScheduled = false;
  const pendingVisibleBySection = new Map();

  function onCardsIntersect(entries) {
    for (const entry of entries) {
      const card = entry.target;
      const sec = findSection(card);
      if (!sec) continue;

      const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      if (isVisible && !card.__animated) {
        if (!pendingVisibleBySection.has(sec)) pendingVisibleBySection.set(sec, new Set());
        pendingVisibleBySection.get(sec).add(card);
      }

      if (!isVisible && !opts.once && card.__animated) {
        card.__animated = false;
        card.classList.remove('is-animated');
        card.style.transitionDelay = '';
      }
    }

    if (!rafScheduled) {
      rafScheduled = true;
      requestAnimationFrame(flushVisibleBatches);
    }
  }

  function flushVisibleBatches() {
    rafScheduled = false;
    for (const [sec, setOfCards] of pendingVisibleBySection.entries()) {
      if (!sec.classList.contains('is-animated')) continue;

      const delayStep = sec.__animOpts?.cardDelayStep ?? opts.cardDelayStep;

      const batch = Array.from(setOfCards)
        .filter(c => !c.__animated)
        .sort((a, b) => (a.__order || 0) - (b.__order || 0));

      batch.forEach((card, idx) => {
        card.style.transitionDelay = `${idx * delayStep}s`;
        card.classList.add('is-animated');
        card.__animated = true;
      });

      setOfCards.clear();
    }
  }

  function getCards(sectionEl) {
    return Array.from(sectionEl.querySelectorAll('[js-animation-block]'));
  }

  function findSection(el) {
    return el.closest('[js-animation]');
  }

  function buildThresholds(minRatio) {
    const steps = 10;
    const arr = [];
    for (let i = 0; i <= steps; i++) arr.push(i / steps);
    if (!arr.includes(minRatio)) arr.push(minRatio);
    return arr.sort((a, b) => a - b);
  }

  function getNumber(val, fallback) {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  }
}

class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.type == "recommendations") {
      this.setRecommendationProducts();
    }
  }

  setRecommendationProducts() {
    const recommendationLink = window.Shopify.routes.root + `recommendations/products?limit=4&product_id=${this.dataset.product}&intent=${this.dataset.recommendations}&section_id=${this.dataset.section}`;
      fetch(recommendationLink)
      .then(response => response.text())
      .then(response => {
        const html = new DOMParser().parseFromString(response, "text/html");

        if (html.querySelector("featured-products[data-empty]")) {
          this.style.display = "none";
          return;
        }
        
        const grid = this.querySelector('[id^="Slider-"]');
        grid.innerHTML = html.querySelector('[id^="Slider-"]').innerHTML;

        this.querySelector("slider-component").update();
      })
      .catch(error => console.error(error))
  }
}

if (document.querySelector("featured-products")) {
  customElements.define("featured-products", FeaturedProduct);
}


class MainCollection extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.productsGrid = this.querySelector('[id^="CollectionGrid-"]');

    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-action]");
      if (!trigger) return;

      switch (trigger.dataset.action) {
        case "switch-collection":
          if (!trigger.dataset.collection) return;
          this.querySelector('[data-featured] .selected')?.classList.remove("selected");
          trigger.classList.add("selected");
          this.filtersUpdate(trigger.dataset.collection);
          break;
        case "open-options":
          this.querySelector("[data-sort]").classList.toggle("open");
          break;
        case "select-option":
          this.querySelector("[name='sort_by']").value = trigger.dataset.option;
          this.querySelector("[data-sort]").classList.remove("open");
          this.filtersUpdate();
          break;
      }
    });

    window.addEventListener("click", (e) => {
      if (!e.target.closest("[data-sort]")) {
        this.querySelector("[data-sort]")?.classList.remove("open");
      }
    });
  }

  filtersUpdate(handle = false) {
    this.toggleLoading();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sort_by", this.querySelector("[name='sort_by']").value);

    let newProductUrl;
    if (handle) {
      searchParams.delete("page");
      // Filters (e.g. the parts finder device) belong to the current collection;
      // carried over to another collection they would hide all its products.
      [...searchParams.keys()]
        .filter((key) => key.startsWith("filter.") || key === "view")
        .forEach((key) => searchParams.delete(key));
      newProductUrl = `${window.Shopify.routes.root}collections/${handle}?${searchParams.toString()}`;
    } else {
      newProductUrl = `${window.location.pathname}?${searchParams.toString()}`;
    }

    window.history.replaceState({}, '', newProductUrl);

    fetch(newProductUrl)
      .then(res => res.text())
      .then(body => {
        const html = new DOMParser().parseFromString(body, "text/html");
        this.renderContent(html);
      })
      .catch(error => console.error(error))
      .finally(() => this.toggleLoading());
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`)
      || html.querySelector("main-collection");
    if (!section) return;

    this.loadSectionAssets(section.closest(".shopify-section") || section.parentElement);

    // Another collection can use another template, so its section id differs;
    // later re-renders (sorting, parts finder) must request the new one.
    if (section.dataset.section) this.dataset.section = section.dataset.section;

    const selectors = [
      "[data-featured-wrapper]",
      "[data-parts-finder-slot]",
      "[data-headings]",
      "[data-grid]",
      "[data-pagination]"
    ];

    selectors.forEach(selector => {
      const current = this.querySelector(selector);
      const incoming = section.querySelector(selector);
      if (current && incoming) {
        current.innerHTML = incoming.innerHTML;
      }
    });

    const relatedSections = [
      ".collection-banner",
      ".collection-seo-proof",
      ".faq"
    ];

    relatedSections.forEach(selector => {
      const current = document.querySelector(selector);
      const incoming = html.querySelector(selector);
      if (current && incoming) {
        current.innerHTML = incoming.innerHTML;
      }
    });

    this.syncBundlePromo(html);
  }

  // Styles and scripts the incoming collection's section needs but the current
  // page didn't load (e.g. the parts finder when switching from a regular collection).
  loadSectionAssets(container) {
    if (!container) return;

    container.querySelectorAll('link[rel="stylesheet"][href], script[src]').forEach((asset) => {
      const attribute = asset.tagName === "LINK" ? "href" : "src";
      const url = asset.getAttribute(attribute);
      if ([...document.querySelectorAll(`[${attribute}]`)].some((el) => el.getAttribute(attribute) === url)) return;

      const element = document.createElement(asset.tagName.toLowerCase());
      [...asset.attributes].forEach(({ name, value }) => element.setAttribute(name, value));
      document.head.appendChild(element);
    });
  }

  syncBundlePromo(html) {
    const current = document.querySelector(".bundle-promo")?.closest(".shopify-section");
    const incoming = html.querySelector(".bundle-promo")?.closest(".shopify-section");

    if (!incoming) {
      current?.remove();
      return;
    }

    const promo = document.importNode(incoming, true);

    if (current) {
      current.replaceWith(promo);
    } else {
      const anchor = this.closest(".shopify-section") || this;
      anchor.parentNode.insertBefore(promo, anchor);
    }
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-collection")) {
  customElements.define("main-collection", MainCollection);
}

class MainBlog extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "switch-blog":
            this.querySelector('[data-tabs] .active').classList.remove("active");
            e.target.classList.add("active");
            this.switchBlog(e.target.dataset.blog);
            break;
        }
      }
    });
  }

  switchBlog(handle) {
    this.toggleLoading();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", 1);
    const newBlogUrl = window.Shopify.routes.root + "blogs/" + handle + "?" + searchParams.toString();

    window.history.replaceState({}, '', "/blogs/" + handle);

    fetch(newBlogUrl)
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");
      this.renderContent(html);
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading());
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      "[data-title]",
      "[data-grid]",
      "[data-pagination]"
    ];

    selectors.forEach(selector => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        this.querySelector(selector).innerHTML = section.querySelector(selector).innerHTML;
      }
    });

    if (document.querySelector(".faq__blocks") && section.querySelector(".faq__blocks")) {
      document.querySelector(".faq__blocks").innerHTML = section.querySelector(".faq__blocks").innerHTML;
    }

    if (document.querySelector(".main-video") && section.querySelector(".main-video")) {
      document.querySelector(".main-video").innerHTML = section.querySelector(".main-video").innerHTML;
      document.querySelector(".main-video").videoInit();
    }
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-blog")) {
  customElements.define("main-blog", MainBlog);
}

class MainArticle extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector("[data-article-content]");
    this.tocList = this.querySelector("[data-article-toc-list]");
    this.headings = this.content
      ? Array.from(this.content.querySelectorAll("h2, h3"))
          .filter((heading) => heading.textContent.trim() !== "")
      : [];

    this.initClickEvents();
    this.initActiveHeadingObserver();
  }

  getHeaderOffset() {
    const menuHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--menu-height")) || 0;
    return menuHeight + 24;
  }

  scrollToPart(partId) {
    const target = document.getElementById(partId);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.pageYOffset - this.getHeaderOffset();
    window.scrollTo({ top, behavior: "smooth" });
    if (history.pushState) history.pushState(null, "", `#${partId}`);
  }

  togglePanel(trigger) {
    const content = trigger.nextElementSibling;
    if (!content) return;
    trigger.classList.toggle("open");
    content.style.maxHeight = trigger.classList.contains("open") ? content.scrollHeight + "px" : null;
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-action]");
      if (!trigger || !this.contains(trigger)) return;

      switch (trigger.dataset.action) {
        case "scroll-to-part":
          this.scrollToPart(trigger.dataset.part);
          break;
        case "toggle-parts":
        case "toggle-question":
          this.togglePanel(trigger);
          break;
      }
    });
  }

  initActiveHeadingObserver() {
    if (!this.headings.length || !this.tocList) return;
    if (!("IntersectionObserver" in window)) return;

    const tocItems = this.tocList.querySelectorAll('[data-action="scroll-to-part"]');
    const setActive = (id) => {
      tocItems.forEach((tocItem) => {
        tocItem.classList.toggle("is-active", tocItem.dataset.part === id);
      });
    };

    const offset = this.getHeaderOffset();
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActive(visible[0].target.id);
      },
      { rootMargin: `-${offset}px 0px -60% 0px`, threshold: 0 }
    );
    this.headings.forEach((heading) => observer.observe(heading));
  }
}

if (document.querySelector("main-article")) {
  customElements.define("main-article", MainArticle);
}

class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;

    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "open-options":
            this.querySelector("[data-sort]").classList.add("open");
            break;
          case "select-option":
            this.querySelector("[name='sort_by']").value = e.target.dataset.option;
            this.filtersUpdate();
            break;
          case "clear-search":
            const input = this.querySelector(".main-search__form-input");
            input.value = "";
            input.focus();
            break;
        }
      }
    });

    window.addEventListener("click", (e) => {
      const sort = this.querySelector("[data-sort]");
      if (sort && !e.target.closest("[data-sort]")) {
        sort.classList.remove("open");
      }
    });
  }

  filtersUpdate() {
    this.toggleLoading();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sort_by", this.querySelector("[name='sort_by']").value);
    const newUrl = window.location.pathname + `?${searchParams.toString()}`;

    window.history.replaceState({}, "", newUrl);

    fetch(newUrl)
      .then((res) => res.text())
      .then((body) => {
        const html = new DOMParser().parseFromString(body, "text/html");
        this.renderContent(html);
      })
      .catch((error) => console.error(error))
      .finally(() => this.toggleLoading());
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      "[data-headings]",
      "[data-grid]",
      "[data-pagination]",
    ];

    selectors.forEach((selector) => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        this.querySelectorAll(selector).forEach((selectorElement, i) => {
          selectorElement.innerHTML = section.querySelectorAll(selector)[i].innerHTML;
        });
      }
    });
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-search")) {
  customElements.define("main-search", MainSearch);
}

// --- COMPONENTS --- //

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute("data-allow-events") === "true";
    this.hideControls = this.getAttribute("data-hide-controls") !== "false"

    this.slider = this.querySelector('[id^="Slider-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.scrollbar = this.querySelector("[slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[slider-scrollbar] span");

    this.pagination = this.querySelector('[slider-pagination]');

    this.counter = this.querySelector('[slider-counter]');

    if (!this.slider) return;

    this.update();
    window.addEventListener('resize', this.update.bind(this));
    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.pagination) {
      this.pagination.addEventListener("click", this.onDotClick.bind(this))
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }
    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute("data-slide")) return;

    const slideIndex = parseInt(event.target.dataset.slide);
    this.scrollToSlide(slideIndex);
  }

  scrollToSlide(index) {
    this.slider.scrollTo({
      left: this.querySelector(`[id^="Slide-"]:nth-child(${index})`).offsetLeft,
      behavior: 'smooth'
    });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const blockWidth = parseFloat(getComputedStyle(items[0]).getPropertyValue("width")) || 0;
    const gap = parseInt(getComputedStyle(this.slider).getPropertyValue("gap")) || 0;
    const hasScroll = this.slider.scrollWidth > clientWidth;
      
    this.maxLength = items.length - (Math.floor((clientWidth + gap) / (blockWidth + gap)) - 1);
    
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;

    items.forEach((item, index) => item.classList.toggle("active", index + 1 == this.currentSlide));
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute("disabled", this.currentSlide == 1);
      this.nextButton.toggleAttribute("disabled", this.currentSlide == this.maxLength);
  
      if (this.hideControls) {
        this.prevButton.classList.toggle("hidden", !hasScroll);
        this.nextButton.classList.toggle("hidden", !hasScroll);
      }
    }

    if (this.scrollbarThumb) {      
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar.classList.toggle("hidden", !hasScroll);
    }

    if (this.pagination) {
      this.updatePagination();
    }

    if (this.counter) {
      this.counter.innerHTML = `${this.currentSlide} / ${items.length}`;
    }
  
    if (this.allowEvents) {
      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: {
            currentSlide: this.currentSlide
          },
          bubbles: true,
        })
      );
    }
  }

  updatePagination() {
    const slides = this.querySelectorAll('[id^="Slide-"]');

    if (!this.slider || slides.length === 0 || !this.pagination) return;

    const containerWidth = this.slider.clientWidth;
    const slideWidth = slides[0].offsetWidth;

    const slidesPerPage = Math.floor(containerWidth / slideWidth);
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle("hidden", totalPages === 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute("data-slide", i);
      if (i === this.currentSlide) dot.classList.add("active");
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define("slider-component", SliderComponent);

/* Cross-fade rotator: the replacement for the two Swiper instances the theme
   used to load on every page (announcement bar, cart drawer benefits). Both
   only ever show one slide at a time, which does not justify shipping a 150 KB
   library site-wide. Slides are stacked in a CSS grid cell, so the element
   keeps the height of its tallest slide and nothing is measured from JS. */
class SlideRotator extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-rotator-slide]"));
    if (!this.slides.length) return;

    this.index = 0;
    this.delay = parseInt(this.dataset.interval, 10) || 0;
    this.next = this.next.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.show(0);

    // A single slide needs no controls, no timer and no dots.
    if (this.slides.length < 2) return;

    this.buildDots();
    this.bindControls();
    this.start();
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  show(index) {
    this.index = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle("is-active", active);
      slide.setAttribute("aria-hidden", active ? "false" : "true");
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => dot.classList.toggle("is-active", i === this.index));
    }
  }

  next() {
    this.show(this.index + 1);
  }

  // Any manual move restarts the timer, so a slide is never cut short.
  goTo(index) {
    this.show(index);
    this.start();
  }

  start() {
    this.stop();
    if (this.delay > 0) this.timer = setInterval(this.next, this.delay);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility() {
    // No point burning a timer while the tab is in the background.
    document.hidden ? this.stop() : this.start();
  }

  bindControls() {
    const prev = this.querySelector("[data-rotator-prev]");
    const next = this.querySelector("[data-rotator-next]");

    if (prev) prev.addEventListener("click", () => this.goTo(this.index - 1));
    if (next) next.addEventListener("click", () => this.goTo(this.index + 1));
  }

  buildDots() {
    const container = this.querySelector("[data-rotator-dots]");
    if (!container) return;

    // The label template comes from Liquid so it stays translated.
    const label = container.dataset.rotatorDots || "";

    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "rotator__dot";
      dot.setAttribute("aria-label", `${label} ${i + 1}`.trim());
      dot.addEventListener("click", () => this.goTo(i));
      container.appendChild(dot);
      return dot;
    });

    this.dots[0].classList.add("is-active");
  }
}

customElements.define("slide-rotator", SlideRotator);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.quickAddButton = this.querySelector('[data-action="quick-add"]');
    this.addToCart = this.addToCart.bind(this);
  }

  connectedCallback() {
    if (this.quickAddButton) {
      this.quickAddButton.addEventListener('click', this.addToCart);
    }
  }

  disconnectedCallback() {
    if (this.quickAddButton) {
      this.quickAddButton.removeEventListener('click', this.addToCart);
    }
  }

  addToCart(event) {
    event.preventDefault();

    if (this.isLoading) return;

    this.toggleLoading();

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const items = [
      {
        id: parseInt(this.quickAddButton.dataset.variant),
        quantity: parseInt(this.quickAddButton.dataset.quantity)
      }
    ];

    // Free gift, one per product, same as the PDP form adds it (see
    // MainProduct.onFormSubmit). _gift_for links it to the product, so the cart
    // drawer keeps their quantities in sync and removes both together.
    const giftVariantId = parseInt(this.quickAddButton.dataset.giftVariant);
    if (giftVariantId) {
      items.unshift({
        id: giftVariantId,
        quantity: items[0].quantity || 1,
        properties: { _gift_for: this.quickAddButton.dataset.variant }
      });
    }

    const cartData = {
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        throw new Error(response.description);
      } else {
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading());
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("product-card", ProductCard);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.onClickEvent = this.onClickEvent.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    // The benefits strip rotates itself — see <slide-rotator> above.
    document.addEventListener("click", this.onDocumentClick);
    this.addEventListener("click", this.onClickEvent);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    this.removeEventListener("click", this.onClickEvent);
  }

  onDocumentClick(event) {
    if (event.target.hasAttribute("cart-drawer-toggle")) {
      this.open();
    }
  }

  updateQuantity(item, quantity, itemEl = false) {
    this.toggleLoading(itemEl);

    let endpoint = 'cart/change.js';
    let data = {
      line: item,
      quantity: quantity,
      sections: ["cart-drawer"],
      sections_url: window.location.pathname
    }

    // Free gifts linked to a product (data-gifts, see cart-drawer.liquid) follow its
    // quantity: the difference is added to the first gift line or taken from the
    // last ones, and removing the product removes them too. A gift on its own is
    // changed like any other line.
    const gifts = (itemEl?.dataset.gifts || "").split(",").filter(Boolean).map(pair => {
      const separator = pair.lastIndexOf(":");
      return { key: pair.slice(0, separator), quantity: parseInt(pair.slice(separator + 1)) };
    });

    if (gifts.length > 0) {
      let delta = quantity - parseInt(itemEl.dataset.quantity);

      if (quantity === 0) {
        gifts.forEach(gift => gift.quantity = 0);
      } else if (delta > 0) {
        gifts[0].quantity += delta;
      } else {
        for (let i = gifts.length - 1; i >= 0 && delta < 0; i--) {
          const taken = Math.min(gifts[i].quantity, -delta);
          gifts[i].quantity -= taken;
          delta += taken;
        }
      }

      const updates = { [itemEl.dataset.key]: quantity };
      gifts.forEach(gift => updates[gift.key] = gift.quantity);

      endpoint = 'cart/update.js';
      data = {
        updates: updates,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      }
    }

    fetch(window.Shopify.routes.root + endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(response => {
      if (response.sections) {
        this.renderContent(response.sections);
      } else {
        throw new Error(response.description);
      }
    })
    .catch((error) => {
      const itemEl = this.querySelector(`[data-item="${item}"]`);
      const messageBlock = itemEl.querySelector("[item-notification]");

      console.error('Error:', error);

      messageBlock.innerText = error.message;
      setTimeout(() => {messageBlock.innerText = ""}, 5000);
    })
    .finally(() => {this.toggleLoading(itemEl)});
  }

  renderContent(sections) {
    if (!sections['cart-drawer']) return;

    const html = new DOMParser().parseFromString(sections['cart-drawer'], "text/html");
    const newCartDrawer = html.querySelector("cart-drawer");
    
    this.querySelector("[js-content-items]").innerHTML = newCartDrawer.querySelector("[js-content-items]").innerHTML;
    this.querySelector("[js-content-footer]").innerHTML = newCartDrawer.querySelector("[js-content-footer]").innerHTML;

    const count = newCartDrawer.dataset.count;
    this.setAttribute("data-count", count);
    document.querySelectorAll("[cart-count],[data-cart-count]").forEach(el => el.innerHTML = count);

    if (this.querySelector('[data-progress="shipping"]')) {
      this.querySelector('[data-progress="shipping"]').style.width = newCartDrawer.querySelector('[data-progress="shipping"]').style.width;
      this.querySelector('.cart-drawer__shipping-label').innerHTML = newCartDrawer.querySelector('.cart-drawer__shipping-label').innerHTML;
    }
  }

  addItemToCart(item, itemEl = false) {
    this.toggleLoading(itemEl);

    const sections = ["cart-drawer"];

    const cartData = {
      items: [
        {
          id: parseInt(item),
          quantity: 1
        }
      ],
      sections: sections,
      sections_url: window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        throw new Error(response.description);
      } else {
        this.renderContent(response.sections);
      }
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading(itemEl));
  }

  onClickEvent(event) {
    if (event.target.hasAttribute("data-action")) {
      event.preventDefault();

      const item = event.target.closest("[data-item]");
      const itemQuantityInput = item?.querySelector('[name="quantity"]') ?? false;
      const itemQuantity = parseInt(itemQuantityInput?.value) ?? false;

      switch (event.target.dataset.action) {
        case "close":
          this.close();
          break;
        case "remove-item":
          if (!this.isLoading) {
            this.updateQuantity(parseInt(item.dataset.item), 0, item);
          }
          break;
        case "decrease-quantity":          
          if (itemQuantity > 0) {
            if (!this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity - 1), item);
            }
          }
          break;
        case "increase-quantity":
          if (itemQuantity < 199) {
            if (!this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity + 1), item);
            }
          }
          break;
        case "add-product":
          if (!this.isLoading) {
            this.addItemToCart(event.target.dataset.variant, event.target);
          }
          break;
        case "to-checkout":
          window.location.pathname = "/checkout";
          break;
      }
    }
  }

  toggleLoading(itemEl) {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading-item", this.isLoading);

    if (itemEl) {
      itemEl.classList.toggle("loading", this.isLoading);
    }
  }

  open() {
    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.classList.remove("open");
    document.documentElement.style.overflow = null;
  }
}

if (document.querySelector("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.onClick);
    document.addEventListener("click", this.onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onOutsideClick);
  }

  onClick(e) {
    const trigger = e.target.closest("[data-action]");
    if (!trigger) return;

    const item = trigger.closest(".localization__item");
    if (!item) return;

    if (trigger.dataset.action === "toggle") {
      const wasOpen = item.classList.contains("open");
      this.closeAll();
      if (!wasOpen) item.classList.add("open");
    } else if (trigger.dataset.action === "select") {
      const form = item.querySelector("form");
      const input = form.querySelector(`[name="${trigger.dataset.name}"]`);
      if (input) input.value = trigger.dataset.value;
      form.submit();
    }
  }

  onOutsideClick(e) {
    if (this.contains(e.target)) return;
    this.closeAll();
  }

  closeAll() {
    this.querySelectorAll(".localization__item").forEach(el => el.classList.remove("open"));
  }
}

if (document.querySelector("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

(function () {
  'use strict';

  var STORAGE_KEY = 'cookie_consent_choices';
  var STORAGE_VERSION = 1;

  // === Storage ===
  function getSavedConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed.version !== STORAGE_VERSION) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveConsent(consent) {
    var payload = {
      version: STORAGE_VERSION,
      timestamp: new Date().toISOString(),
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      personalization: !!consent.personalization
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('cookie-consent: localStorage unavailable', e);
    }
    return payload;
  }

  // Last consent state dispatched to Google/GTM/trackers — used for deduplication.
  // Prevents firing consent_update multiple times for the same state (Nick van Maaren spec).
  // Seeded from analytics.liquid's early inline push (if a saved consent existed) so we
  // don't double-fire consent_update during init().
  var lastDispatchedConsent = window.__earlyConsentDispatched || null;

  function consentsEqual(a, b) {
    if (!a || !b) return false;
    return !!a.analytics === !!b.analytics
        && !!a.marketing === !!b.marketing
        && !!a.personalization === !!b.personalization;
  }

  // === Google Consent Mode v2 ===
  function pushToGoogleConsentMode(consent) {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.personalization ? 'granted' : 'denied',
      functionality_storage: consent.personalization ? 'granted' : 'denied',
      personalization_storage: consent.personalization ? 'granted' : 'denied'
    });
  }

  // === dataLayer event for GTM triggers ===
  function pushConsentUpdateEvent(consent) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'consent_update',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.personalization ? 'granted' : 'denied'
    });
  }

  // Single entry point for propagating consent state. Dedupes by last dispatched state
  // so the same consent doesn't get pushed twice (e.g., once from init, once from the
  // Shopify visitorConsentCollected echo of our own setTrackingConsent call).
  function dispatchConsent(consent) {
    if (consentsEqual(lastDispatchedConsent, consent)) return false;
    lastDispatchedConsent = {
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      personalization: !!consent.personalization
    };
    pushToGoogleConsentMode(consent);
    pushConsentUpdateEvent(consent);
    loadConsentGatedTrackers(consent);
    return true;
  }

  // === Shopify Customer Privacy API ===
  function syncShopifyCustomerPrivacy(consent) {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return;
        try {
          window.Shopify.customerPrivacy.setTrackingConsent({
            analytics: consent.analytics,
            marketing: consent.marketing,
            preferences: consent.personalization,
            sale_of_data: consent.marketing
          }, function () {});
        } catch (e) {
          console.warn('cookie-consent: setTrackingConsent failed', e);
        }
      }
    );
  }

  // === Consent-gated tracker loaders (Hotjar, Clarity, Mida, Klaviyo) ===
  // Registered in snippets/analytics.liquid. Called once consent is known.
  function loadConsentGatedTrackers(consent) {
    if (!window.__achateTrackers) return;
    if (consent.analytics) window.__achateTrackers.loadAnalytics();
    if (consent.marketing) window.__achateTrackers.loadMarketing();
  }

  function applyConsent(consent) {
    var saved = saveConsent(consent);
    dispatchConsent(saved);
    syncShopifyCustomerPrivacy(saved);
  }

  // === DOM helpers ===
  function getToggle(name) {
    var el = document.querySelector('[data-cookie-toggle="' + name + '"]');
    return el ? !!el.checked : false;
  }
  function setToggle(name, value) {
    var el = document.querySelector('[data-cookie-toggle="' + name + '"]');
    if (el) el.checked = !!value;
  }

  // === Page lock (blocker + scroll) ===
  function lockPage() {
    var blocker = document.getElementById('cookie-blocker');
    if (blocker) blocker.hidden = false;
  }
  function unlockPage() {
    var blocker = document.getElementById('cookie-blocker');
    if (blocker) blocker.hidden = true;
  }

  // === Visibility ===
  function showBanner() {
    var banner = document.getElementById('cookie-banner');
    if (banner) banner.hidden = false;
    // Cookie wall removed — banner is shown but does not block interaction with the page.
    // Visitors can browse/interact without making a consent choice first (per client request).
  }
  function hideBanner() {
    var el = document.getElementById('cookie-banner');
    if (el) el.hidden = true;
  }

  function showModal() {
    var saved = getSavedConsent();
    setToggle('analytics', saved ? saved.analytics : false);
    setToggle('marketing', saved ? saved.marketing : false);
    setToggle('personalization', saved ? saved.personalization : false);

    hideBanner();
    var el = document.getElementById('cookie-modal');
    if (!el) return;
    el.hidden = false;
    lockPage();
    var firstToggle = el.querySelector('[data-cookie-toggle]:not([disabled])');
    if (firstToggle) firstToggle.focus();
  }

  function hideModal() {
    var el = document.getElementById('cookie-modal');
    if (el) el.hidden = true;
  }

  function dismissAll() {
    hideModal();
    hideBanner();
    unlockPage();
  }

  // === Actions ===
  function acceptAll() {
    applyConsent({ analytics: true, marketing: true, personalization: true });
    dismissAll();
  }
  function rejectAll() {
    applyConsent({ analytics: false, marketing: false, personalization: false });
    dismissAll();
  }
  function savePreferences() {
    applyConsent({
      analytics: getToggle('analytics'),
      marketing: getToggle('marketing'),
      personalization: getToggle('personalization')
    });
    dismissAll();
  }

  // GDPR jurisdictions (EU + EEA + UK + Switzerland).
  var GDPR_COUNTRIES = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
    'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
    'GB','IS','LI','NO','CH'
  ];
  var COUNTRY_CACHE_KEY = 'visitor_country';

  // Fetch visitor country by IP via Cloudflare's public trace endpoint.
  // No auth, no rate limit, returns plain text with "loc=XX" line.
  function fetchVisitorCountry(callback) {
    // sessionStorage cache — avoid repeated fetch on every pageview in a session
    try {
      var cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
      if (cached !== null) return callback(cached);
    } catch (e) {}

    var done = false;
    var timer = setTimeout(function () {
      if (done) return;
      done = true;
      callback('');
    }, 2000);

    try {
      fetch('https://www.cloudflare.com/cdn-cgi/trace', { cache: 'no-store' })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          var m = text.match(/loc=([A-Z]{2})/);
          var country = m ? m[1] : '';
          try { sessionStorage.setItem(COUNTRY_CACHE_KEY, country); } catch (e) {}
          callback(country);
        })
        .catch(function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          callback('');
        });
    } catch (e) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      callback('');
    }
  }

  // Banner shows only in GDPR jurisdictions (detected by real IP).
  // Non-GDPR visitors get implicit consent so Shopify pixel + GA/GTM fire.
  // If country cannot be determined — fall back to showing banner (GDPR-safe).
  function shouldShowBanner(callback) {
    fetchVisitorCountry(function (country) {
      if (!country) return callback(true);
      callback(GDPR_COUNTRIES.indexOf(country) !== -1);
    });
  }

  // === Event bindings ===
  function bindUIEvents() {
    document.querySelectorAll('[data-cookie-accept-all]').forEach(function (el) {
      el.addEventListener('click', acceptAll);
    });
    document.querySelectorAll('[data-cookie-reject-all]').forEach(function (el) {
      el.addEventListener('click', rejectAll);
    });
    document.querySelectorAll('[data-cookie-manage]').forEach(function (el) {
      el.addEventListener('click', showModal);
    });
    document.querySelectorAll('[data-cookie-save-prefs]').forEach(function (el) {
      el.addEventListener('click', savePreferences);
    });
  }

  // === External listener: checkout/other surfaces ===
  // Shopify fires this when customer privacy state changes — including when WE call
  // setTrackingConsent ourselves. We must NOT treat our own echo as a real user choice,
  // otherwise we pollute localStorage with auto-denied state and the banner never re-appears.
  document.addEventListener('visitorConsentCollected', function (event) {
    if (!event || !event.detail) return;
    var shopifyConsent = {
      analytics: !!event.detail.analyticsAllowed,
      marketing: !!event.detail.marketingAllowed,
      personalization: !!event.detail.preferencesAllowed
    };
    // First-visit auto-deny detection: if event reports all-denied AND nothing is saved,
    // this is the early setTrackingConsent(denied) call from analytics.liquid, not a user choice.
    // Skip — don't persist denied state, don't push consent_update.
    var hasAnyConsent = shopifyConsent.analytics || shopifyConsent.marketing || shopifyConsent.personalization;
    if (!hasAnyConsent && !getSavedConsent()) return;

    saveConsent(shopifyConsent);
    dispatchConsent(shopifyConsent);
  });

  // === Debug: force banner via ?cookie=force or theme editor ===
  function isForced() {
    try {
      if (/[?&]cookie=force\b/.test(window.location.search)) return true;
      if (window.Shopify && window.Shopify.designMode) return true;
    } catch (e) {}
    return false;
  }

  // === Init ===
  function init() {
    var saved = getSavedConsent();
    var forced = isForced();

    if (saved && !forced) {
      // Returning visitor with a saved choice — replay it once, before GTM tags evaluate.
      // consent_default is already in dataLayer from analytics.liquid; we follow it with
      // a single consent_update carrying the saved values (Nick van Maaren spec).
      dispatchConsent(saved);
      syncShopifyCustomerPrivacy(saved);
    } else if (forced) {
      // Theme editor / ?cookie=force debug mode — always show the banner.
      // Only sync denied state to Shopify if there's a saved consent to override
      // (so pixels don't fire while testing). Otherwise analytics.liquid already denied.
      if (saved) syncShopifyCustomerPrivacy({ analytics: false, marketing: false, personalization: false });
      showBanner();
    } else {
      shouldShowBanner(function (show) {
        if (show) {
          // GDPR region, no saved choice yet — analytics.liquid already denied Shopify
          // customer privacy on load. Do NOT call setTrackingConsent here: it would
          // trigger visitorConsentCollected and persist denied state to localStorage,
          // which blocks the banner from re-appearing on refresh.
          showBanner();
        } else {
          // Non-GDPR region. Behavior controlled by theme setting "Strict consent mode":
          //   OFF (default): auto-grant so Shopify pixel + GA/GTM fire immediately.
          //   ON: do nothing — Shopify default consent state takes over
          //       (used when Advanced Consent Mode is configured via GTM).
          if (!window.__COOKIE_STRICT_CONSENT) {
            applyConsent({ analytics: true, marketing: true, personalization: true });
          }
        }
      });
    }

    bindUIEvents();
  }

  // Public API — allows "Manage cookies" link in footer to reopen modal
  window.cookieConsent = {
    open: showModal,
    reset: function () {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      showBanner();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Lazy autoplay for decorative videos.
   These are GIF replacements: they should look like they autoplay, but must not
   download until they are close to the viewport. Markup renders them with
   preload="none" and a poster, so nothing is fetched until play() is called. */
(function () {
  const SELECTOR = "video.js-lazy-video";

  function init() {
    const videos = document.querySelectorAll(SELECTOR);
    if (!videos.length) return;

    if (!("IntersectionObserver" in window)) {
      videos.forEach((video) => video.play().catch(() => {}));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting) {
            // preload="none" means there is nothing buffered yet; play() kicks
            // off the download and starts as soon as enough data has arrived.
            video.play().catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: "200px 0px" }
    );

    videos.forEach((video) => observer.observe(video));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
